import { create } from "zustand";
import deleteBlobService from "@/services/storage/deleteBlob";
import uploadBlobService from "@/services/storage/uploadBlob";
import { BlobItem } from "@azure/storage-blob";
import { MAX_FILE_SIZE } from "@/lib/constants";

type BlobState = {
  blobs: BlobItem[];
  loading: boolean;
  error: string | null;
  uploading: boolean;
  
  // Delete confirmation state
  deleteConfirmation: {
    isOpen: boolean;
    blobToDelete: BlobItem | null;
  };
  
  // Actions
  setBlobs: (blobs: BlobItem[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  setUploading: (uploading: boolean) => void;
  
  // Delete confirmation actions
  openDeleteConfirmation: (blob: BlobItem) => void;
  closeDeleteConfirmation: () => void;
  confirmDelete: () => Promise<void>;
  
  // Upload actions
  uploadBlobs: (files: File[], blobName?: string, metadata?: Record<string, string>) => Promise<boolean>;
};

export const BlobStore = create<BlobState>((set, get) => ({
  // Initial state
  blobs: [],
  loading: false,
  error: null,
  uploading: false,
  deleteConfirmation: {
    isOpen: false,
    blobToDelete: null,
  },

  // Basic actions
  setBlobs: (blobs) => set({ blobs }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),
  setUploading: (uploading) => set({ uploading }),

  // Delete confirmation actions
  openDeleteConfirmation: (blob) => set({
    deleteConfirmation: {
      isOpen: true,
      blobToDelete: blob,
    }
  }),
  
  closeDeleteConfirmation: () => set({
    deleteConfirmation: {
      isOpen: false,
      blobToDelete: null,
    }
  }),
  
  confirmDelete: async () => {
    const { deleteConfirmation, closeDeleteConfirmation, setLoading, setError } = get();
    
    if (deleteConfirmation.blobToDelete) {
      try {
        setLoading(true);
        setError(null);

        const result = await deleteBlobService(deleteConfirmation.blobToDelete.name);

        if (result.status === "OK") {
          // TanStack Query will automatically refresh the blobs list
          closeDeleteConfirmation();
        } else {
          const errorMessage = result.errors?.[0]?.message || "Failed to delete blob";
          setError(errorMessage);
          closeDeleteConfirmation();
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
        setError(errorMessage);
        closeDeleteConfirmation();
      } finally {
        setLoading(false);
      }
    }
  },

  // Upload actions
  uploadBlobs: async (files: File[], blobName?: string, metadata?: Record<string, string>): Promise<boolean> => {
    const { setUploading, setError } = get();

    // File size validation
    const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE);
    
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => f.name).join(', ');
      const maxSizeMB = Math.round(MAX_FILE_SIZE / (1024 * 1024));
      setError(`The following files are too large (max ${maxSizeMB}MB): ${fileNames}`);
      return false;
    }
    
    try {
      setUploading(true);
      setError(null);

      let successCount = 0;
      let failedFiles: string[] = [];

      // Upload files sequentially to avoid overwhelming the server
      for (const file of files) {
        try {
          // For single file uploads, use the provided blobName, for multiple files use original filename
          const finalBlobName = files.length === 1 ? blobName : undefined;
          const result = await uploadBlobService(file, finalBlobName, metadata);
          
          if (result.status === "OK") {
            successCount++;
          } else {
            failedFiles.push(file.name);
          }
        } catch (err) {
          failedFiles.push(file.name);
        }
      }

      // TanStack Query will automatically refresh the blobs list

      if (failedFiles.length > 0) {
        if (files.length === 1) {
          // Single file failed
          const errorMessage = `Failed to upload "${failedFiles[0]}"`;
          setError(errorMessage);
          return false;
        } else {
          // Multiple files, some failed
          setError(`${successCount} files uploaded successfully. Failed to upload: ${failedFiles.join(', ')}`);
          return failedFiles.length < files.length; // Return true if at least some files succeeded
        }
      }

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred during upload";
      setError(errorMessage);
      return false;
    } finally {
      setUploading(false);
    }
  },
}));